/**
 * @fileoverview Organization Management Hooks
 * 
 * Comprehensive hooks for managing organizations, memberships, and settings.
 * Provides three main hooks for different aspects of organization management:
 * 
 * 1. useOrganizations - CRUD operations for organizations
 * 2. useOrganizationMemberships - Member management and role assignment
 * 3. useOrganizationSettings - Key-value settings management
 * 
 * Organizations represent companies (clients or vendors) using the platform.
 * Each organization can have multiple users with different roles (owner, super_user, member).
 * 
 * Key Features:
 * - Organization lifecycle management (create, read, update, soft delete)
 * - Member enumeration with enriched profile data
 * - Role-based access control (owner, super_user, member)
 * - Flexible settings storage with key-value pairs
 * - Integration with billing and user limits
 * 
 * @module useOrganizations
 */

import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';

/**
 * Represents an organization (client or vendor company)
 */
export interface Organization {
  id: string;
  name: string;
  description?: string | null;
  status: string | null;
  settings: any;
  created_by_admin_id?: string | null;
  billing_plan: string | null;
  max_users: number | null;
  is_deleted: boolean | null;
  created_at: string | null;
  updated_at: string | null;
}

/**
 * Basic organization membership record
 */
export interface OrganizationMembership {
  id: string;
  user_id: string;
  organization_id: string;
  org_role: 'owner' | 'super_user' | 'member';
  created_at: string;
  updated_at: string;
}

/**
 * Organization member with enriched profile data
 * Includes user details from profiles table for display purposes
 */
export interface EnrichedOrganizationMember {
  id: string; // user_id as id for DataTable compatibility
  user_id: string;
  email: string;
  org_role: 'owner' | 'super_user' | 'member';
  platform_role: string;
  display_name: string;
  first_name: string | null;
  last_name: string | null;
  business_name: string | null;
  created_at: string;
  updated_at: string;
}

/**
 * Represents an invitation to join an organization
 */
export interface OrganizationInvitation {
  id: string;
  organization_id: string;
  email: string;
  role: 'owner' | 'super_user' | 'member' | 'read_only';
  invited_by_user_id: string;
  expires_at: string;
  status: 'pending' | 'accepted' | 'expired' | 'cancelled';
  invitation_token: string;
  created_at: string;
  updated_at: string;
}

/**
 * Hook for managing organizations
 * 
 * Provides CRUD operations for organizations including:
 * - Listing all organizations (sorted by creation date)
 * - Creating new organizations
 * - Updating organization details
 * - Soft deleting organizations (marks as deleted, doesn't remove)
 * 
 * Organizations are the top-level entity representing companies.
 * They contain users (via organization_user_roles), settings, and
 * are associated with jobs, RFQs, and other business entities.
 * 
 * Soft Delete:
 * Organizations are never permanently deleted, only marked with is_deleted=true.
 * This preserves historical data and referential integrity.
 * 
 * @returns Object with organizations array, loading state, and CRUD functions
 * 
 * @example
 * ```tsx
 * function OrganizationList() {
 *   const { organizations, loading, createOrganization } = useOrganizations();
 * 
 *   const handleCreate = async () => {
 *     await createOrganization({
 *       name: 'Acme Corp',
 *       description: 'A great company',
 *       billing_plan: 'pro',
 *       max_users: 10
 *     });
 *   };
 * 
 *   return (
 *     <div>
 *       {organizations.map(org => (
 *         <OrganizationCard key={org.id} {...org} />
 *       ))}
 *     </div>
 *   );
 * }
 * ```
 */
export const useOrganizations = () => {
  const [organizations, setOrganizations] = useState<Organization[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const { toast } = useToast();

  const fetchOrganizations = async () => {
    try {
      setLoading(true);
      const { data, error } = await supabase
        .from('organizations')
        .select('*')
        .order('created_at', { ascending: false });

      if (error) throw error;
      setOrganizations(data || []);
    } catch (err: any) {
      setError(err.message);
      toast({
        title: "Error",
        description: "Failed to fetch organizations",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const createOrganization = async (orgData: { name: string; description?: string; [key: string]: any }) => {
    try {
      const { data, error } = await supabase
        .from('organizations')
        .insert([orgData] as any)
        .select()
        .single();

      if (error) throw error;
      
      setOrganizations(prev => [data, ...prev]);
      toast({
        title: "Success",
        description: "Organization created successfully",
      });
      
      return data;
    } catch (err: any) {
      toast({
        title: "Error",
        description: "Failed to create organization",
        variant: "destructive",
      });
      throw err;
    }
  };

  const updateOrganization = async (id: string, updates: Partial<Organization>) => {
    try {
      const { data, error } = await supabase
        .from('organizations')
        .update(updates)
        .eq('id', id)
        .select()
        .single();

      if (error) throw error;
      
      setOrganizations(prev => 
        prev.map(org => org.id === id ? { ...org, ...data } : org)
      );
      
      toast({
        title: "Success",
        description: "Organization updated successfully",
      });
      
      return data;
    } catch (err: any) {
      toast({
        title: "Error",
        description: "Failed to update organization",
        variant: "destructive",
      });
      throw err;
    }
  };

  const deleteOrganization = async (id: string) => {
    try {
      const { error } = await supabase
        .from('organizations')
        .update({ is_deleted: true })
        .eq('id', id);

      if (error) throw error;
      
      setOrganizations(prev => prev.filter(org => org.id !== id));
      toast({
        title: "Success",
        description: "Organization deleted successfully",
      });
    } catch (err: any) {
      toast({
        title: "Error",
        description: "Failed to delete organization",
        variant: "destructive",
      });
      throw err;
    }
  };

  useEffect(() => {
    fetchOrganizations();
  }, []);

  return {
    organizations,
    loading,
    error,
    fetchOrganizations,
    createOrganization,
    updateOrganization,
    deleteOrganization,
  };
};

/**
 * Hook for managing organization memberships and member roles
 * 
 * Provides functionality for:
 * - Fetching enriched member list (includes names, emails, roles)
 * - Updating member roles (owner, super_user, member)
 * - Removing members from the organization
 * 
 * Member Data:
 * Uses security definer RPC function `get_org_members_admin` to fetch
 * enriched member data that combines user profiles with organization roles.
 * This bypasses RLS for admin access while maintaining security.
 * 
 * Role Hierarchy:
 * - owner: Full control, can manage all settings and members
 * - super_user: Can manage most settings and jobs
 * - member: Basic access to jobs and organization resources
 * 
 * @param organizationId - The organization ID to manage members for
 * @returns Object with enriched members array and member management functions
 * 
 * @example
 * ```tsx
 * function MemberManagement({ orgId }) {
 *   const {
 *     memberships,
 *     loading,
 *     updateMemberRole,
 *     removeMember
 *   } = useOrganizationMemberships(orgId);
 * 
 *   const promoteToAdmin = (userId: string) => {
 *     updateMemberRole(userId, 'super_user');
 *   };
 * 
 *   return (
 *     <table>
 *       {memberships.map(member => (
 *         <tr key={member.user_id}>
 *           <td>{member.display_name}</td>
 *           <td>{member.email}</td>
 *           <td>{member.org_role}</td>
 *           <td>
 *             <button onClick={() => promoteToAdmin(member.user_id)}>
 *               Promote
 *             </button>
 *           </td>
 *         </tr>
 *       ))}
 *     </table>
 *   );
 * }
 * ```
 */
export const useOrganizationMemberships = (organizationId?: string) => {
  const [memberships, setMemberships] = useState<EnrichedOrganizationMember[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const { toast } = useToast();

  const fetchMemberships = async () => {
    if (!organizationId) return;
    
    try {
      setLoading(true);
      setError(null);

      // Use security definer function to get enriched member data
      const { data, error } = await supabase.rpc('get_org_members_admin' as any, {
        org_id: organizationId
      });

      if (error) throw error;
      // Add id field for DataTable compatibility
      const enrichedData = Array.isArray(data) ? data.map((m: any) => ({ ...m, id: m.user_id })) : [];
      setMemberships(enrichedData);
    } catch (err: any) {
      setError(err.message);
      toast({
        title: "Error",
        description: "Failed to fetch memberships",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const updateMemberRole = async (userId: string, orgRole: 'owner' | 'super_user' | 'member') => {
    try {
      const { error } = await supabase
        .from('organization_user_roles')
        .update({ org_role: orgRole })
        .eq('user_id', userId)
        .eq('organization_id', organizationId);

      if (error) throw error;
      
      setMemberships(prev => 
        prev.map(m => m.user_id === userId ? { ...m, org_role: orgRole } : m)
      );
      
      toast({
        title: "Success",
        description: "Member role updated successfully",
      });
    } catch (err: any) {
      toast({
        title: "Error",
        description: "Failed to update member role",
        variant: "destructive",
      });
    }
  };

  const removeMember = async (userId: string) => {
    try {
      const { error } = await supabase
        .from('organization_user_roles')
        .delete()
        .eq('user_id', userId)
        .eq('organization_id', organizationId);

      if (error) throw error;
      
      setMemberships(prev => prev.filter(m => m.user_id !== userId));
      toast({
        title: "Success",
        description: "Member removed successfully",
      });
    } catch (err: any) {
      toast({
        title: "Error",
        description: "Failed to remove member",
        variant: "destructive",
      });
    }
  };

  useEffect(() => {
    fetchMemberships();
  }, [organizationId]);

  return {
    memberships,
    loading,
    error,
    fetchMemberships,
    updateMemberRole,
    removeMember,
  };
};

/**
 * Hook for managing organization-specific settings
 * 
 * Provides a flexible key-value store for organization-specific configuration.
 * Settings are stored in the organization_settings table with keys and JSON values.
 * 
 * Common Setting Keys:
 * - notification_preferences: User notification configuration
 * - workflow_defaults: Default values for jobs and RFQs
 * - branding: Company colors, logo URLs, etc.
 * - integration_config: Third-party integration settings
 * 
 * Upsert Behavior:
 * `updateSettings` uses upsert to create or update settings atomically,
 * preventing duplicate key errors and simplifying the API.
 * 
 * @param organizationId - The organization ID to manage settings for
 * @returns Object with settings object and update function
 * 
 * @example
 * ```tsx
 * function OrganizationSettings({ orgId }) {
 *   const { settings, loading, updateSettings } = useOrganizationSettings(orgId);
 * 
 *   const saveNotificationPrefs = async (prefs: any) => {
 *     await updateSettings('notification_preferences', prefs);
 *   };
 * 
 *   return (
 *     <div>
 *       <h3>Current Settings</h3>
 *       <pre>{JSON.stringify(settings, null, 2)}</pre>
 *       
 *       <button onClick={() => saveNotificationPrefs({ email: true, sms: false })}>
 *         Update Notifications
 *       </button>
 *     </div>
 *   );
 * }
 * ```
 */
export const useOrganizationSettings = (organizationId?: string) => {
  const [settings, setSettings] = useState<any>({});
  const [loading, setLoading] = useState(true);
  const { toast } = useToast();

  const fetchSettings = async () => {
    if (!organizationId || organizationId === "placeholder-org-id") {
      setLoading(false);
      return;
    }
    
    try {
      setLoading(true);
      const { data, error } = await supabase
        .from('organization_settings')
        .select('*')
        .eq('organization_id', organizationId);

      if (error) throw error;
      
      const settingsObj = data?.reduce((acc, setting) => {
        acc[setting.setting_name] = setting.setting_value;
        return acc;
      }, {}) || {};
      
      setSettings(settingsObj);
    } catch (err: any) {
      toast({
        title: "Error",
        description: "Failed to fetch settings",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const updateSettings = async (settingName: string, settingValue: any) => {
    if (!organizationId) return;
    
    try {
      const { data: user } = await supabase.auth.getUser();
      if (!user.user) throw new Error('No authenticated user');

      const { error } = await supabase
        .from('organization_settings')
        .upsert({
          organization_id: organizationId,
          setting_name: settingName,
          setting_value: settingValue,
          created_by_user_id: user.user.id,
          updated_by_user_id: user.user.id,
        });

      if (error) throw error;
      
      setSettings(prev => ({
        ...prev,
        [settingName]: settingValue,
      }));
      
      toast({
        title: "Success",
        description: "Settings updated successfully",
      });
    } catch (err: any) {
      toast({
        title: "Error",
        description: "Failed to update settings",
        variant: "destructive",
      });
    }
  };

  useEffect(() => {
    fetchSettings();
  }, [organizationId]);

  return {
    settings,
    loading,
    fetchSettings,
    updateSettings,
  };
};